import { parseSharePointLink } from "./sharepoint-link";
import type { EntityRefStyle } from "./entity-refs";

// ============================================================================
// Smart-link provider registry (server-safe, zero Tiptap runtime imports).
//
// A smart link is a pasted URL the entity chip editor renders as a chip
// instead of a plain autolink. Everything provider-specific lives in ONE
// entry here: the URL parser, the chip style, the extra attributes the
// serializer emits, and the vocabulary line that teaches the model what the
// tag means. Adding a
// provider (Google Drive, Notion, …) means adding an entry to
// SMART_LINK_PROVIDERS — the paste handler, chip rendering, serializer, and
// prompt assembly all iterate this registry.
// ============================================================================

/** A parsed smart link: what the chip stores (id = url) and displays. */
export interface SmartLink {
  /** The pasted URL, verbatim — the canonical id of the chip. */
  url: string;
  /** Display label derived from the URL (file name, site, host, …). */
  label: string;
}

export interface SmartLinkProvider<K extends string = string> {
  /**
   * Stable provider key. Doubles as the chip's `refType` (drives the
   * `data-ref-type` CSS) and as the serialized tag name (`<sharepoint …>`).
   */
  key: K;
  /** Chip presentation, merged into ENTITY_REF_STYLES. */
  style: EntityRefStyle;
  /** Parse pasted text; null when it isn't this provider's URL. */
  parse(text: string): SmartLink | null;
  /**
   * Extra model-facing tag attributes derived from the URL (e.g. a decoded
   * `path` for SharePoint, a file `id` for Google Drive). Nullish values are
   * omitted from the tag.
   */
  serializeAttrs?(url: string): Record<string, string | null | undefined>;
  /** Bullet line for the model's reference vocabulary (see prompts.ts). */
  vocabulary: string;
}

const sharepoint = {
  key: "sharepoint",
  style: {
    color: "#0f6cbd",
    background: "oklch(0.55 0.16 255 / 0.12)",
    iconSvg: "%3Cpath d='M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z'/%3E",
  },
  parse: parseSharePointLink,
  serializeAttrs: (url: string) => ({ path: parseSharePointLink(url)?.path }),
  vocabulary:
    '<sharepoint url="…" path="…">Name</sharepoint> — a SharePoint or OneDrive file/folder ' +
    "pinned by URL; `path` (when present) is the already-decoded server-relative path. To read " +
    "or download it: find the registered site whose URL is a prefix of `url` via " +
    "`sharepoint_list_sites`, pick the document library with `sharepoint_list_drives`, then " +
    "call `sharepoint_import_file` with the drive-relative remainder of `path`. URLs on a " +
    "`…-my.sharepoint.com` host under `/personal/…` are personal OneDrive items — use the " +
    "`onedrive_*` tools for those. If no matching Microsoft 365 integration or site is " +
    "available, say so rather than guessing at the file's contents.",
} satisfies SmartLinkProvider<"sharepoint">;

/** Every smart-link provider. Order matters: first parse() match wins. */
export const SMART_LINK_PROVIDERS = [sharepoint] as const;

export type SmartLinkProviderKey = (typeof SMART_LINK_PROVIDERS)[number]["key"];

/** Chip styles per provider key, merged into ENTITY_REF_STYLES by types.ts. */
export function smartLinkStyles(): Record<SmartLinkProviderKey, EntityRefStyle> {
  // Safe by construction: SmartLinkProviderKey derives from this same array.
  return Object.fromEntries(SMART_LINK_PROVIDERS.map((p) => [p.key, p.style])) as Record<
    SmartLinkProviderKey,
    EntityRefStyle
  >;
}

/** Match pasted text against every provider; null when no provider claims it. */
export function parseSmartLink(
  text: string,
): { provider: SmartLinkProvider; link: SmartLink } | null {
  for (const provider of SMART_LINK_PROVIDERS) {
    const link = provider.parse(text);
    if (link) return { provider, link };
  }
  return null;
}

export function smartLinkProvider(key: string): SmartLinkProvider | null {
  return SMART_LINK_PROVIDERS.find((p) => p.key === key) ?? null;
}

export function isSmartLinkRefType(refType: string): refType is SmartLinkProviderKey {
  return SMART_LINK_PROVIDERS.some((p) => p.key === refType);
}

/** The registry's contribution to the model's reference vocabulary. */
export function smartLinkVocabulary(): string {
  return SMART_LINK_PROVIDERS.map((p) => `- ${p.vocabulary}`).join("\n");
}
